import React, { useState } from "react";
import Layout from "../../Shared/Layout";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@material-ui/core";
import styled from "styled-components";
import { Trans } from "../../utils/i18n";
import Wrapper from "./Wrapper";
import dayjs from "dayjs";
import { Alert, Button, InputAdornment, Modal, Snackbar } from "@mui/material";
import { formatCurrency } from "../../utils/formatCurrency";
import { useTranslation } from "react-i18next";
import { axiosClient } from "../../utils/axiosClient";
import { Inertia } from "@inertiajs/inertia";
import { transformErrorResponse } from "../../utils/transformErrorResponse";
import { FORM_ERROR } from "final-form";
import { Field, Form as FinalForm } from "react-final-form";
import { isNil } from "lodash";
import TextInput from "../../Shared/TextInput";
import Select from "../../Shared/Select";

const HR = styled.hr`
    display: flex;
    justify-content: flex-end;
    margin: 15px 0;
`;

export default function Payments({
    order,
    payments,
    permissions,
}) {
    const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState(null);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const { t } = useTranslation();

    const onSubmitRefund = async (data) => {
        if (!selectedPayment || !selectedPayment?.order_id) return;

        try {
            const response = await axiosClient.post(
                route("order.refund", { order: selectedPayment.order_id, payment: selectedPayment.id }),
                data
            );

            setSnackbarMessage(response?.data?.message || <Trans i18nKey="queueDetail.refund.success">Refund successful</Trans>);
            Inertia.reload();
            setSelectedPayment(null);
            setIsRefundModalOpen(false);
        } catch (error) {
            if (error?.response?.data?.errors) {
                return transformErrorResponse(error.response.data.errors);
            }

            return {
                [FORM_ERROR]: error?.response?.data?.message || t('common.error')
            };
        }
    };

    return (
        <Wrapper>
            <Snackbar
                open={!!snackbarMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                autoHideDuration={6000}
                onClose={() => setSnackbarMessage(null)}
            >
                <Alert
                    onClose={() => setSnackbarMessage(null)}
                    severity="success"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            <Modal open={isRefundModalOpen} onClose={() => {
                setSelectedPayment(null);
                setIsRefundModalOpen(false);
            }}>
                <div className="bg-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-6 w-full max-w-screen-md rounded-md shadow-md">
                <FinalForm
                    initialValues={{
                        amount: selectedPayment?.amount ? (selectedPayment?.amount / 100).toFixed(2) : 0,
                        reason: '',
                    }}
                    onSubmit={onSubmitRefund}
                    validate={values => {
                        const errors = {}
                        if (isNil(values.amount)) {
                          errors.amount = t('common.required');
                        }

                        if (!values.reason) {
                            errors.reason = t('common.required');
                        }

                        if (!values.reason_text) {
                            errors.reason_text = t('common.required');
                        }

                        return errors;
                    }}
                >
                    {({
                        handleSubmit,
                        submitting,
                        submitError
                    }) => {
                        return (
                            <form onSubmit={handleSubmit}>
                                {submitError && (
                                    <Alert severity="error" className="mb-4">
                                        {submitError}
                                    </Alert>
                                )}
                                <div className="space-y-4">
                                    <h3 className="m-0">
                                        <Trans i18nKey="queueDetail.refund">
                                            Refund
                                        </Trans>
                                    </h3>
                                    <div className="grid grid-cols-12 gap-4">
                                        <div className="col-span-6">
                                            <Field
                                                name={"amount"}
                                                type={"number"}
                                                component={TextInput}
                                                field={{
                                                    isCurrency: true,
                                                    InputProps: {
                                                        endAdornment: <InputAdornment position="start">{(selectedPayment?.currency || selectedPayment?.payment_intent?.currency || "").toUpperCase()}</InputAdornment>,
                                                    },
                                                    label: (
                                                        <Trans i18nKey="queueDetail.refund.amount">
                                                            Amount
                                                        </Trans>
                                                    ),
                                                    required: true,
                                                    type: "number",
                                                }}
                                                disabled={submitting}
                                            />
                                            </div>
                                            <div className="col-span-6">
                                                <Field
                                                    name={"reason"}
                                                    type={"select"}
                                                    component={Select}
                                                    field={{
                                                        disableUnselected: false,
                                                        type: "select",
                                                        label: (
                                                            <Trans i18nKey="queueDetail.refund.reason">
                                                                Reason
                                                            </Trans>
                                                        ),
                                                        required: true,
                                                        options: [
                                                            {
                                                                value: 'requested_by_customer',
                                                                label: <Trans i18nKey="queueDetail.refund.reason.requested_by_customer">Requested by customer</Trans>,
                                                            },
                                                            {
                                                                value: 'duplicate',
                                                                label: <Trans i18nKey="queueDetail.refund.reason.duplicate">Duplicate payment</Trans>,
                                                            },
                                                            {
                                                                value: 'fraudulent',
                                                                label: <Trans i18nKey="queueDetail.refund.reason.fraudulent">Fraudulent</Trans>,
                                                            },
                                                        ],
                                                    }}
                                                    disabled={submitting}
                                                />
                                            </div>
                                            <div className="col-span-12">
                                                <Field
                                                    name={"reason_text"}
                                                    type={"text"}
                                                    component={TextInput}
                                                    field={{
                                                        label: (
                                                            <Trans i18nKey="queueDetail.refund.reason">
                                                                Reason Text
                                                            </Trans>
                                                        ),
                                                        type: "text",
                                                        multiline: true,
                                                        required: true,
                                                    }}
                                                    disabled={submitting}
                                                />
                                            </div>
                                    </div>
                                </div>

                                <div className={'flex justify-end gap-2 mt-10'}>
                                    <Button
                                        variant="contained"
                                        disabled={submitting}
                                        type="button"
                                        onClick={() => {
                                            setSelectedPayment(null);
                                            setIsRefundModalOpen(false);
                                        }}
                                    >
                                        <Trans i18nKey="queueDetail.cancel">
                                            Cancel
                                        </Trans>
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color={"secondary"}
                                        disabled={submitting}
                                        type={"submit"}
                                    >
                                        <Trans i18nKey="queueDetail.refund.submit">
                                            Refund
                                        </Trans>
                                    </Button>
                                </div>
                            </form>
                        );
                    }}
                </FinalForm>
                </div>
            </Modal>

            <HR />
            <h3 className={"flex justify-between items-center"}>
                <Trans i18nKey="queueDetail.tabs.payments">Payments</Trans>
            </h3>

            <TableContainer>
                <Table
                    size="small"
                    aria-label={
                        <Trans i18nKey="queueDetail.collapsibleTable">
                            collapsible table
                        </Trans>
                    }
                >
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <Trans i18nKey="common.date">Date</Trans>
                            </TableCell>
                            <TableCell>
                                <Trans i18nKey="common.amount">Amount</Trans>
                            </TableCell>
                            <TableCell>
                                <Trans i18nKey="queueDetail.status">
                                    Status
                                </Trans>
                            </TableCell>
                            <TableCell>
                                <Trans i18nKey="queueDetail.refund_reason">
                                    Refund reason
                                </Trans>
                            </TableCell>
                            <TableCell>
                                <Trans i18nKey="queueDetail.refunded_amount">
                                    Refunded Amount
                                </Trans>
                            </TableCell>
                            <TableCell>
                                <Trans i18nKey="queueDetail.refunded_by">
                                    Refunded by
                                </Trans>
                            </TableCell>
                            <TableCell>
                                <Trans i18nKey="queueDetail.refunded_at">
                                    Refunded at
                                </Trans>
                            </TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {payments?.map((payment) => (
                            <>
                                <TableRow key={payment.id}>
                                    <TableCell component="th" scope="row">
                                        {dayjs(payment.created_at).format(
                                            "DD.MM.YYYY HH:mm"
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {formatCurrency(
                                            payment.amount,
                                            payment.currency || payment?.payment_intent?.currency
                                        )}
                                    </TableCell>
                                    <TableCell>{payment.status}</TableCell>
                                    <TableCell>
                                        <div>{payment.refund_reason}</div>
                                        <div>{payment.refund_reason_text}</div>
                                    </TableCell>
                                    <TableCell>{payment.refunded_amount && formatCurrency(payment.refunded_amount, payment.currency)}</TableCell>
                                    <TableCell>{payment?.refunded_by?.name}</TableCell>
                                    <TableCell>{payment.refunded_at && dayjs(payment.refunded_at).format("DD.MM.YYYY HH:mm")}</TableCell>
                                    <TableCell>
                                    {order.permissions.payment.refund && (
                                        <Button
                                            variant={"outlined"}
                                            color={"secondary"}
                                            size="small"
                                            disabled={payment.status === "refunded"}
                                            onClick={() => {
                                                setSelectedPayment(payment);
                                                setIsRefundModalOpen(true);
                                            }}
                                        >
                                            <Trans i18nKey="queueDetail.refund">
                                                Refund
                                            </Trans>
                                        </Button>
                                    )}
                                    </TableCell>
                                </TableRow>
                            </>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Wrapper>
    );
}

Payments.layout = (page) => <Layout children={page} />;
